import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UIFilter, UIFilterWithSubFiltersFilter } from 'src/app/people/quick-build.service';
import { DWColumn } from 'src/app/shared/dw-column';
import {
  getPeopleColumnByName,
  SearchFilterOperator,
  SupportedOperators,
} from '../../people-columns';
import { Log } from 'src/app/logger.service';
import {
  RoleDefinition,
  RoleDefinitionsService,
} from 'src/app/core/services/role-definitions.service';
import { StoredItemsBrowserComponent } from 'src/app/shared/components/stored-items-browser/stored-items-browser.component';
import { filter, take, tap } from 'rxjs';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-filter-subfilters',
    templateUrl: './filter-subfilters.component.html',
    styleUrls: ['./filter-subfilters.component.scss'],
    standalone: false
})
export class FilterSubfiltersComponent implements OnInit, OnDestroy {
  @Input() filter: UIFilterWithSubFiltersFilter;
  @Input() operator: 'and' | 'or';
  @Input() depth: number = 0;
  @Output() removeFilter = new EventEmitter<UIFilter>();
  @HostBinding('class') class =
    'simple-filter-component filter-row query-item tw-relative tw-flex tw-gap-1 tw-mb-5';

  column: DWColumn;
  availableOperators: SearchFilterOperator[] = [];
  selectedOperator: SearchFilterOperator;

  // For job_role field
  selectedRole: RoleDefinition | null = null;
  selectedRoleId: string | null = null;
  selectedRoleName: string | null = null;

  // Subscription management
  private subscription = new Subscription();

  constructor(
    private dialog: MatDialog,
    private roleDefinitionsService: RoleDefinitionsService
  ) {}

  ngOnInit() {
    try {
      this.column = getPeopleColumnByName(this.filter.filter.field!);
    } catch (e) {
      Log.e('Filter field not found');
      return;
    }
    this.setAvailableOperators();
    this.loadFilterParams();
    this.setFilterParams();
  }

  get fieldType(): string | undefined {
    return this.filter.filter.field;
  }

  get isJobRoleField(): boolean {
    return this.fieldType === 'job_role';
  }

  setAvailableOperators() {
    this.availableOperators = [SupportedOperators.equals];

    // Set default operator
    if (!this.selectedOperator) {
      this.selectedOperator = this.availableOperators[0];
    }
  }

  loadFilterParams() {
    if (!this.filter.filter) {
      return;
    }

    // Load the operator based on the filter configuration
    const operator = this.getOperatorForFilter();
    this.selectedOperator = operator || this.availableOperators[0];

    // Load field-specific parameters
    if (this.isJobRoleField && this.filter.filter.id) {
      // Load the definition from the filter params, but then try to dynamically load it
      this.selectedRoleId = this.filter.filter.id;
      this.filter.filter.filters = this.filter.filter.filters;
      this.selectedRoleName = this.filter.filter.name || null;

      this.loadRoleDefinition(this.selectedRoleId);
    }
  }

  loadRoleDefinition(roleId: string) {
    this.subscription.add(
      this.roleDefinitionsService
        .getById(roleId)
        .pipe(
          filter((role) => role !== null),
          take(1),
          tap((role) => {
            if (role) {
              this.selectedRole = role;
            }
          })
        )
        .subscribe()
    );
  }

  getOperatorForFilter(): SearchFilterOperator | null {
    return SupportedOperators.equals;
  }

  setFilterParams() {
    this.filter.filter.field = 'job_role';
    if (this.selectedRole) {
      this.filter.filter.id = this.selectedRole.id;
      this.filter.filter.name = this.selectedRole.name || '';
      this.filter.filter.filters = RoleDefinitionsService.toQuickBuildFilterGroup(
        this.selectedRole
      );
    } else {
      this.filter.filter.id = undefined;
      this.filter.filter.name = undefined;
      this.filter.filter.filters = undefined;
    }
    Log.d('FilterSubfiltersComponent setFilterParams', this.filter);
  }

  onRoleSelected(role: RoleDefinition | null) {
    this.selectedRole = role;
    this.selectedRoleId = role?.id || null;
    this.selectedRoleName = role?.name || null;
    this.setFilterParams();
  }

  onViewAllRoles() {
    const dialogRef = this.dialog.open(StoredItemsBrowserComponent, {
      width: '95vw',
      height: '90vh',
      maxWidth: '1200px',
      maxHeight: '1000px',
      panelClass: 'saved-lists-dialog',
      backdropClass: 'tw-bg-black/80',
      data: {
        items$: this.roleDefinitionsService.roleDefinitions$,
        config: this.roleDefinitionsService.getBrowserConfig(),
      },
    });

    this.subscription.add(
      dialogRef.afterClosed().subscribe((result: RoleDefinition | undefined) => {
        if (result) {
          this.onRoleSelected(result);
        }
      })
    );
  }

  onRemove() {
    this.removeFilter.emit(this.filter);
  }

  isEvenDepth(): boolean {
    return this.depth % 2 === 0;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
